import React, {
  useState,
  useMemo,
  useContext,
  useEffect,
  useCallback,
} from "react";
import AuthContext from "../Context/AuthProvider";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Tooltip,
  IconButton,
  Fab,
  ListSubheader,
  CardHeader,
  Divider,
  useMediaQuery,
  useTheme,
  Box,
  CardActions,
  Grid2,
} from "@mui/material";
import dayjs from "dayjs";
import { paymentModes, refundPaymentModes } from "../Utility/Constants";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { getBillById, viewBillById } from "../API/BillingApi";
import { useSnackbar } from "../Utility/UserMessageContext";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DataGrid } from "@mui/x-data-grid";
import { PaymentModes } from "../Utility/Constants";
import ConfirmationDialog from "../common/ConfirmationDialog";
import ServiceTableSection from "./ServiceTableSection";
import PaymentTableSection from "./PaymentTableSection";
import RefundTableSection from "./RefundTableSection";
import { numberToWords } from "../Utility/Utility";
import { formatAge } from "../Utility/Utility";
import { settleBill } from "../API/BillingApi";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css"; // Import your custom styles
import GenericTextField from "../common/GenericTextField";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";
import GenericDropDown from "../common/GenericDropDown";
import GenericFileUploader from "../common/GenericFileUploader";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import LoadingScreen from "../Utility/LoadingScreen";

const NewBill = ({ newBillInfo = {}, billId }) => {
  const authContext = useContext(AuthContext);
  const { auth } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [patientId, setPatientId] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visit, setVisit] = useState(null);
  const [plan, setPlan] = useState(null);
  const paymentOptions = Object.values(PaymentModes);
  const [isSaving, setIsSaving] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [showSpinner, setShowSpinner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const showLoading = isLoading || showSpinner;

  const [billAmountSummary, setBillAmountSummary] = useState({
    totalAmount: 0,
    paidAmount: 0,
    refundedAmount: 0,
    balanceAmount: 0,
  });

  // New states to hold total amounts from respective sections
  const [paymentSectionTotalPaid, setPaymentSectionTotalPaid] = useState(0);
  const [refundSectionTotalRefunded, setRefundSectionTotalRefunded] =
    useState(0);
  const [serviceSectionTotalBilled, setServiceSectionTotalBilled] = useState(0);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState({ table: "", id: null });
  const [patientInfo, setPatientInfo] = useState(null);
  const [billDetails, setBillDetails] = useState(null);
  // const formattedDate = billDetails?.createdOn
  //   ? dayjs(billDetails.createdOn).format("DD MMM YYYY hh:mm A").toUpperCase()
  //   : newBillInfo?.createdOn
  //   ? dayjs(newBillInfo.createdOn).format("DD MMM YYYY hh:mm A").toUpperCase()
  //   : "N/A";

  useEffect(() => {
    const loadBillingData = async () => {
      // Ensure you handle the valid billId or newBillInfo.id
      const currentBillId = billId || newBillInfo?.id;

      if (!currentBillId) {
        setLoading(false);
        console.warn("No valid Bill ID to fetch billing data.");
        return;
      }

      setLoading(true);
      try {
        const result = await getBillById(auth, currentBillId);

        if (result.isSuccess) {
          const data = result.data;
          setPatientId(data.visit?.patientId || data.plan?.patientId || null);
          setVisits(
            (data.visit && [data.visit]) ||
              (data.plan && data.plan.visits) ||
              []
          );
          setVisit(data.visit || null);
          setPlan(data.plan || null);
          setBillDetails({
            billNo: data.displayId || "N/A",
            createdBy: data.createdByUserName || "N/A",
            createdOn: data.createdOn || "N/A",
            billId: data.id || "N/A",
          });

          setPatientInfo({
            //displayId: data.visit?.displayId || "N/A",
            patientName: `${
              data.visit?.patientFirstName ||
              data.plan?.patientFirstName ||
              "N/A"
            } ${
              data.visit?.patientLastName || data.plan?.patientLastName || ""
            }`,
            phone:
              data.visit?.patientMobileNo ||
              data.plan?.patientMobileNo ||
              "N/A",
            age:
              (data.visit && data.visit?.patientAgeInYears) ||
              (data.plan && data.plan?.patientAgeInYears) ||
              "N/A",
            gender:
              data.visit?.patientGender || data.plan?.patientGender || "N/A",
            address: data.visit?.patient?.address || "N/A",
            patientId: data.visit?.patientId || null,
          });

          const updatedSummary = {
            totalAmount: data.totalAmount ?? 0,
            paidAmount: data.paidAmount ?? 0,
            refundedAmount: data.totalRefundedAmount ?? 0,
            balanceAmount: data.balanceAmount ?? 0,
          };
          setBillAmountSummary(updatedSummary);

          // Set initial values for the section-specific states
          setServiceSectionTotalBilled(data.totalAmount ?? 0);
          setPaymentSectionTotalPaid(data.paidAmount ?? 0);
          setRefundSectionTotalRefunded(data.refundedAmount ?? 0);
        } else {
          setError("Failed to load billing details");
          console.error("API call failed:", result.data);
        }
      } catch (error) {
        console.error("Error fetching billing details", error);
        setError("Error fetching billing details");
      } finally {
        setLoading(false);
      }
    };
    loadBillingData();
  }, [billId]);

  // const handleDeleteRow = (id, table) => {
  //   setRowToDelete({ id, table });
  //   setOpenDeleteDialog(true);
  // };

  // const confirmDelete = async () => {
  //   setOpenDeleteDialog(false);
  //   refreshBillingData();
  // };

  const handleSettleBill = async () => {
    if (!newBillInfo?.id && !billId) {
      showSnackbar("Missing bill ID. Cannot settle the bill.", "error");
      return;
    }

    const currentBillId = newBillInfo?.id || billId;
    setIsSaving(true);

    const response = await settleBill(auth, currentBillId);

    if (response.isSuccess) {
      showSnackbar("Bill settled successfully!", "success");
      await refreshBillingData();
    } else {
      showSnackbar(response.data || "❌ Failed to settle the bill.", "error");
    }

    setIsSaving(false);
  };

  const handlePrintInvoice = async () => {
    showSnackbar("Generating invoice for printing...", "info");
    try {
      await viewBillById(auth, billId || newBillInfo.id);
    } catch (error) {
      showSnackbar("Failed to generate invoice.", "error");
      console.error("Print Invoice Error:", error);
    }
  };

  const refreshBillingData = async () => {
    // Ensure you handle the valid billId or newBillInfo.id
    const currentBillId = billId || newBillInfo?.id;

    if (!currentBillId) {
      setLoading(false);
      console.warn("No valid Bill ID to fetch billing data.");
      return;
    }

    setLoading(true);
    try {
      const result = await getBillById(auth, currentBillId);

      if (result.isSuccess) {
        const data = result.data;
        setPatientId(data.visit?.patientId || data.plan?.patientId || null);
        setVisits(
          (data.visit && [data.visit]) || (data.plan && data.plan.visits) || []
        );
        setVisit(data.visit || null);
        setPlan(data.plan || null);
        setBillDetails({
          billNo: data.displayId || "N/A",
          createdBy: data.createdByUserName || "N/A",
          createdOn: data.createdOn || "N/A",
          billId: data.id || "N/A",
        });

        setPatientInfo({
          //displayId: data.visit?.displayId || "N/A",
          patientName: `${
            data.visit?.patientFirstName || data.plan?.patientFirstName || "N/A"
          } ${data.visit?.patientLastName || data.plan?.patientLastName || ""}`,
          phone:
            data.visit?.patientMobileNo || data.plan?.patientMobileNo || "N/A",
          age:
            data.visit?.patientAgeInYears ||
            data.plan?.patientAgeInYears ||
            "N/A",
          gender:
            data.visit?.patientGender || data.plan?.patientGender || "N/A",
          address: data.visit?.patient?.address || "N/A",
          patientId: data.visit?.patientId || data.plan?.patientId || null,
        });

        const updatedSummary = {
          totalAmount: data.totalAmount ?? 0,
          paidAmount: data.paidAmount ?? 0,
          refundedAmount: data.totalRefundedAmount ?? 0,
          balanceAmount: data.balanceAmount ?? 0,
        };
        setBillAmountSummary(updatedSummary);

        // Set initial values for the section-specific states
        setServiceSectionTotalBilled(data.totalAmount ?? 0);
        setPaymentSectionTotalPaid(data.paidAmount ?? 0);
        setRefundSectionTotalRefunded(data.refundedAmount ?? 0);
      } else {
        setError("Failed to load billing details");
        console.error("API call failed:", result.data);
      }
    } catch (error) {
      console.error("Error fetching billing details", error);
      setError("Error fetching billing details");
    } finally {
      setLoading(false);
    }
  };

  // Use the new section-specific states for display and balance calculation
  //const totalAmount = serviceSectionTotalBilled.toFixed(2);
  const totalAmount = useMemo(() => {
    const sum = visits
      .flatMap((visit) => visit.availedServices)
      .reduce(
        (acc, service) =>
          acc +
          (service.fees -
            (service.fees * (service.discountInPercentage || 0)) / 100),
        0
      );
    return sum?.toFixed(2);
  }, [visits]);

  const totalPaidAmount = paymentSectionTotalPaid.toFixed(2);
  const totalRefundAmount = refundSectionTotalRefunded.toFixed(2);
  const totalBalance = (
    totalAmount -
    paymentSectionTotalPaid +
    refundSectionTotalRefunded
  ).toFixed(2);

  return (
    <Grid2
      container
      spacing={isMobile ? 1 : 2}
      sx={{ marginX: "0", marginY: "0", width: "100%" }}
    >
      {loading && (<LoadingScreen />)}

      <Grid2 container size={12}>
        <Grid2 size={12}>
          <MenuBar
            showSettleBill={true}
            onSettleBill={handleSettleBill}
            showPrint={true}
            onPrint={handlePrintInvoice}
          />
        </Grid2>
      </Grid2>

      <Grid2
        container
        size={12}
        spacing={2}
        sx={{ maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}
      >
        {/* Patient Information & Bill Information Cards */}
        <Card
          className="outerCard"
          variant="outlined"
          sx={{ width: "100%", overflow: "auto", ml: "10px", padding: "10px" }}
        >
          <Grid2
            container
            size={12}
            sx={{
              maxHeight: "calc(100vh - 150px)",
              overflowY: "auto",
            }}
          >
            {/* Combined Patient and Bill Information Card */}
            <Grid2 size={12}>
              <Card
                sx={{
                  padding: "16px",
                  pointerEvents: showLoading ? "none" : "auto",
                  opacity: showLoading ? 0.6 : 1,
                }}
                variant="outlined"
              >
                <Grid2 container size={12}>
                  {/* Patient Information Section */}
                  <Grid2 size={12}>
                    <Typography variant="body1" fontWeight="bold" gutterBottom>
                      Patient Information
                    </Typography>
                    {/* Patient Information in Horizontal Layout */}
                    <Grid2 container size={12} spacing={2}>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        <HeaderValueDisplay
                          header="Patient Name"
                          value={
                            patientInfo?.patientName ||
                            `${newBillInfo?.patientFirstName || ""} ${
                              newBillInfo?.patientLastName || ""
                            }`
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        <HeaderValueDisplay
                          header="Gender"
                          value={
                            patientInfo?.gender || newBillInfo.gender || "N/A"
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        <HeaderValueDisplay
                          header="Age"
                          value={formatAge(
                            patientInfo?.age || newBillInfo?.age
                          )}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        <HeaderValueDisplay
                          header={
                            visit?.displayId
                              ? "Visit No"
                              : plan?.displayId
                              ? "Plan No"
                              : "Visit/Plan No"
                          }
                          value={
                            visit?.displayId ||
                            plan?.displayId ||
                            newBillInfo.visitDisplayId ||
                            "N/A"
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        <HeaderValueDisplay
                          header="Phone No"
                          value={
                            patientInfo?.phone ||
                            newBillInfo.patientMobileNumber ||
                            "N/A"
                          }
                        />
                      </Grid2>
                    </Grid2>
                  </Grid2>

                  {/* Bill Information Section - New Line */}
                  <Grid2 size={12}>
                    <Typography variant="body1" fontWeight="bold">
                      Bill Information
                    </Typography>
                    {/* Bill Information in Horizontal Layout */}
                    <Grid2 container size={12} spacing={2}>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        <HeaderValueDisplay
                          header="Bill No"
                          value={
                            billDetails?.billNo ||
                            newBillInfo.displayId ||
                            "N/A"
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        <HeaderValueDisplay
                          header="Bill Date"
                          value={
                            (billDetails?.createdOn &&
                              convertUTCToTimeZoneDateTimeString(
                                billDetails.createdOn,
                                authContext.auth.timeZone
                              )) ||
                            (newBillInfo?.createdOn &&
                              convertUTCToTimeZoneDateTimeString(
                                newBillInfo.createdOn,
                                authContext.auth.timeZone
                              )) ||
                            "N/A"
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        <HeaderValueDisplay
                          header="Created By"
                          value={
                            billDetails?.createdBy ||
                            newBillInfo.createdByUserName ||
                            "N/A"
                          }
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        {/* Empty space for alignment */}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 }}>
                        {/* Empty space for alignment */}
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Grid2>
              </Card>
            </Grid2>
          </Grid2>

          {/* Amount Summary Cards */}
          <Grid
            item
            xs={12}
            container
            spacing={isMobile ? 1 : 2}
            sx={{
              mt: isMobile ? 1 : 2,
              maxHeight: "calc(100vh - 150px)",
              overflowY: "auto",
            }}
          >
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ width: "100%" }}>
                <CardHeader
                  subheader="Total Billed Amount"
                  subheaderTypographyProps={{
                    align: "center",
                    variant: "body2",
                    fontWeight: "bold",
                  }}
                  sx={{ paddingY: "10px", textAlign: "center" }}
                ></CardHeader>
                <CardContent>
                  <Typography variant="h4" align="center">
                    Rs {totalAmount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ width: "100%" }}>
                <CardHeader
                  subheader="Total Paid Amount"
                  subheaderTypographyProps={{
                    align: "center",
                    variant: "body2",
                    fontWeight: "bold",
                  }}
                  sx={{ paddingY: "10px", textAlign: "center" }}
                ></CardHeader>
                <CardContent>
                  <Typography variant="h4" align="center">
                    Rs {totalPaidAmount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ width: "100%" }}>
                <CardHeader
                  subheader="Total Refunded Amount"
                  subheaderTypographyProps={{
                    align: "center",
                    variant: "body2",
                    fontWeight: "bold",
                  }}
                  sx={{ paddingY: "10px", textAlign: "center" }}
                ></CardHeader>
                <CardContent>
                  <Typography variant="h4" align="center">
                    Rs {totalRefundAmount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined" sx={{ width: "100%" }}>
                <CardHeader
                  subheader="Total Balance"
                  subheaderTypographyProps={{
                    align: "center",
                    variant: "body2",
                    fontWeight: "bold",
                  }}
                  sx={{ paddingY: "10px", textAlign: "center" }}
                ></CardHeader>
                <CardContent>
                  <Typography variant="h4" align="center">
                    Rs {totalBalance}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Services Section */}
          <Grid
            item
            xs={12}
            sx={{
              mt: isMobile ? 1 : 2,
              maxHeight: "calc(100vh - 150px)",
              overflowY: "auto",
            }}
          >
            <ServiceTableSection
              billId={newBillInfo.id || billId}
              onServiceDataChange={refreshBillingData}
              isSmallTablet={isSmallTablet}
              visits={visits}
              patientId={patientId}
              onTotalBilledChange={setServiceSectionTotalBilled} // Pass the setter function here
            />
          </Grid>

          {/* Payments Section */}
          <Grid
            item
            xs={12}
            sx={{
              mt: isMobile ? 1 : 2,
              maxHeight: "calc(100vh - 150px)",
              overflowY: "auto",
            }}
          >
            <PaymentTableSection
              billId={newBillInfo.id || billId}
              showSnackbar={showSnackbar}
              patientId={patientId}
              onPaymentDataChange={refreshBillingData}
              onTotalPaidChange={setPaymentSectionTotalPaid}
              isSmallTablet={isSmallTablet}
            />
          </Grid>

          {/* Refunds Section */}
          <Grid2
            item
            xs={12}
            sx={{
              mt: isMobile ? 1 : 2,
              maxHeight: "calc(100vh - 150px)",
              overflowY: "auto",
            }}
          >
            <RefundTableSection
              billId={newBillInfo.id || billId}
              showSnackbar={showSnackbar}
              onRefundDataChange={refreshBillingData}
              onTotalRefundChange={setRefundSectionTotalRefunded} // Pass the setter function here
              isSmallTablet={isSmallTablet}
            />
          </Grid2>
        </Card>
      </Grid2>
    </Grid2>
  );
};

export default NewBill;
